import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { InstagramIcon, ExternalLinkIcon, SmartphoneIcon, ChevronDownIcon, StarIcon, YoutubeIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { TikTokIcon, SpotifyIcon, XLogoIcon, CustomFacebookIcon as FacebookIcon } from './shared/Icons';
import { CATEGORIES, accounts, featuredCreators, SocialCategory, SocialAccount } from '../data/social-accounts';
function FeaturedSpotlight() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  if (featuredCreators.length === 0) return null;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    delay: 0.3
  }} className="max-w-5xl mx-auto mb-6" data-id="element-2645">
      <div className="rounded-2xl border-l-4 border-amber-400 bg-white shadow-md overflow-hidden" data-id="element-2646">
        {/* Accordion header button */}
        <button onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} className="flex items-center justify-between px-5 py-4 text-left hover:bg-amber-50/50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" data-id="element-2647">
          <div className="flex items-center gap-3" data-id="element-2648">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-2649">
              <StarIcon className="w-5 h-5 text-white fill-white" data-id="element-2650" />
            </div>
            <span className="font-bebas text-2xl tracking-wider text-amber-600" data-id="element-2651">
              {isEs ? 'Destacados: Artistas, Narradores y Agentes de Cambio' : 'Featured: Artists, Storytellers & Changemakers'}
            </span>
            <span className="font-nunito text-xs text-gray-400 font-semibold ml-2 bg-amber-50 px-2 py-1 rounded-full" data-id="element-2652">
              {featuredCreators.length}{' '}
              {isEs ? featuredCreators.length === 1 ? 'creador' : 'creadores' : featuredCreators.length === 1 ? 'creator' : 'creators'}
            </span>
          </div>
          <motion.div animate={{
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex-shrink-0 ml-2" data-id="element-2653">
            <ChevronDownIcon className="w-5 h-5 text-amber-500" data-id="element-2654" />
          </motion.div>
        </button>

        {/* Collapsible content */}
        <AnimatePresence initial={false} data-id="element-2655">
          {isOpen && <motion.div key="featured-content" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="overflow-hidden" data-id="element-2656">
              <div className="border-t border-amber-100 px-5 py-5 bg-amber-50/30" data-id="element-2657">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2658">
                  {featuredCreators.map((creator, i) => {
                const hasLinks = creator.instagramUrl || creator.tiktokUrl || creator.youtubeUrl || creator.spotifyUrl || creator.xUrl || creator.facebookUrl;
                return <motion.div key={creator.handle} initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.4,
                  delay: i * 0.08
                }} className="relative bg-white rounded-2xl shadow-sm border-2 border-amber-200 overflow-hidden flex flex-col" data-id="element-2659">
                        {/* Gold accent stripe */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400" data-id="element-2660" />

                        {/* Creator info header */}
                        <div className="p-4 pt-5" data-id="element-2661">
                          <div className="flex items-center gap-3 mb-2" data-id="element-2662">
                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${creator.imageGradient} flex items-center justify-center flex-shrink-0 shadow-sm text-white font-bebas text-lg tracking-wider ring-2 ring-amber-300 ring-offset-1`} data-id="element-2663">
                              {creator.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2664">
                              <h3 className="font-bebas text-lg text-haa-navy tracking-wider leading-none truncate" data-id="element-2665">
                                {creator.name}
                              </h3>
                              <p className="font-nunito text-[11px] font-bold text-gray-500 truncate" data-id="element-2666">
                                {creator.handle}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-1.5 bg-amber-50 rounded-lg px-2.5 py-1.5" data-id="element-2667">
                            <StarIcon className="w-3 h-3 text-amber-500 fill-amber-500 flex-shrink-0 mt-0.5" data-id="element-2668" />
                            <p className="font-nunito text-[11px] text-amber-800 leading-snug italic line-clamp-2" data-id="element-2669">
                              {isEs ? creator.featuredReasonEs : creator.featuredReason}
                            </p>
                          </div>
                        </div>

                        {/* Embed area */}
                        {creator.embedUrl ? <div className="flex-1 border-t border-amber-100 bg-gray-50/50 min-h-0" data-id="element-2670">
                            <iframe src={creator.embedUrl} className="w-full h-[380px]" frameBorder="0" scrolling="no" allowTransparency allow="encrypted-media" title={`${creator.name} embed`} data-id="element-2671" />
                          </div> : <div className="flex-1 border-t border-amber-100 bg-gray-50/50 flex items-center justify-center p-6 min-h-[200px]" data-id="element-2672">
                            <p className="font-nunito text-xs text-gray-400 text-center" data-id="element-2673">
                              {isEs ? 'Embed próximamente' : 'Embed coming soon'}
                            </p>
                          </div>}

                        {/* Platform links footer */}
                        {hasLinks && <div className="flex items-center justify-center gap-3 py-3 border-t border-amber-100" data-id="element-2674">
                            {creator.instagramUrl && <a href={creator.instagramUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100 transition-colors" title="Instagram" data-id="element-2675">
                                <InstagramIcon className="w-4 h-4" data-id="element-2676" />
                              </a>}
                            {creator.tiktokUrl && <a href={creator.tiktokUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors" title="TikTok" data-id="element-2677">
                                <TikTokIcon className="w-4 h-4" data-id="element-2678" />
                              </a>}
                            {creator.youtubeUrl && <a href={creator.youtubeUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors" title="YouTube" data-id="element-2679">
                                <YoutubeIcon className="w-4 h-4" data-id="element-2680" />
                              </a>}
                            {creator.spotifyUrl && <a href={creator.spotifyUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors" title="Spotify" data-id="element-2681">
                                <SpotifyIcon className="w-4 h-4" data-id="element-2682" />
                              </a>}
                            {creator.xUrl && <a href={creator.xUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors" title="X" data-id="element-2683">
                                <XLogoIcon className="w-4 h-4" data-id="element-2684" />
                              </a>}
                            {creator.facebookUrl && <a href={creator.facebookUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Facebook" data-id="element-2685">
                                <FacebookIcon className="w-4 h-4" data-id="element-2686" />
                              </a>}
                          </div>}
                      </motion.div>;
              })}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}
function SocialCard({
  account,
  index
}: {
  account: SocialAccount;
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const isInstagram = account.platform === 'Instagram';
  const isYouTube = account.platform === 'YouTube';
  const showBio = isHovered || isExpanded;
  return <motion.article layout initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.9
  }} transition={{
    duration: 0.4,
    delay: index * 0.05,
    ease: [0.16, 1, 0.3, 1]
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={() => setIsExpanded(!isExpanded)} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-300 flex flex-col cursor-pointer overflow-hidden h-fit" data-id="element-2687">
      <div className="p-4 flex items-center gap-3" data-id="element-2688">
        {/* Avatar */}
        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${account.imageGradient} flex items-center justify-center flex-shrink-0 shadow-sm text-white font-bebas text-xl tracking-wider`} data-id="element-2689">
          {account.name.charAt(0)}
        </div>

        {/* Name & Handle */}
        <div className="flex-1 min-w-0" data-id="element-2690">
          <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-none truncate" data-id="element-2691">
            {account.name}
          </h3>
          <p className="font-nunito text-xs font-bold text-gray-500 mt-1 truncate" data-id="element-2692">
            {account.handle}
          </p>
        </div>

        {/* Platform Icon */}
        <div className={`p-2 rounded-full flex-shrink-0 ${isInstagram ? 'bg-pink-50 text-pink-600' : isYouTube ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-800'}`} data-id="element-2693">
          {isInstagram ? <InstagramIcon className="w-4 h-4" data-id="element-2694" /> : isYouTube ? <YoutubeIcon className="w-4 h-4" data-id="element-2695" /> : <TikTokIcon className="w-4 h-4" data-id="element-2696" />}
        </div>
      </div>

      {/* Expandable Bio */}
      <AnimatePresence data-id="element-2697">
        {showBio && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-2698">
            <div className="px-4 pb-4 pt-1 border-t border-gray-50 mt-1" data-id="element-2699">
              <p className="font-nunito text-gray-600 text-sm leading-relaxed mb-3" data-id="element-2700">
                {isEs ? account.bioEs : account.bio}
              </p>
              <a href={account.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center justify-center gap-1.5 w-full py-2 bg-gray-50 hover:bg-gray-100 text-haa-navy font-nunito font-bold text-xs rounded-lg transition-colors border border-gray-200" data-id="element-2701">
                {isEs ? `Seguir en ${account.platform}` : `Follow on ${account.platform}`}
                <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-2702" />
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.article>;
}
function CategoryAccordion({
  category,
  accounts,
  index
}: {
  category: SocialCategory;
  accounts: SocialAccount[];
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const [isOpen, setIsOpen] = useState(index === 0);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const Icon = category.icon;
  const isEmpty = accounts.length === 0;
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 24
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className={`rounded-2xl border-l-4 ${category.borderColor} bg-white shadow-md overflow-hidden mb-4`} data-id="element-2703">
      <button onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" data-id="element-2704">
        <div className="flex items-center gap-3" data-id="element-2705">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`} data-id="element-2706">
            <Icon className="w-5 h-5 text-white" data-id="element-2707" />
          </div>
          <span className={`font-bebas text-2xl tracking-wider ${category.accentColor}`} data-id="element-2708">
            {isEs ? category.labelEs : category.label}
          </span>
          {isEmpty ? <span className="font-nunito text-xs text-gray-400 font-semibold ml-2 bg-gray-100 px-2 py-1 rounded-full" data-id="element-2709">
              {isEs ? 'Próximamente' : 'Coming soon'}
            </span> : <span className="font-nunito text-xs text-gray-400 font-semibold ml-2 bg-gray-100 px-2 py-1 rounded-full" data-id="element-2710">
              {accounts.length}{' '}
              {isEs ? accounts.length === 1 ? 'creador' : 'creadores' : accounts.length === 1 ? 'creator' : 'creators'}
            </span>}
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex-shrink-0 ml-2" data-id="element-2711">
          <ChevronDownIcon className={`w-5 h-5 ${category.accentColor}`} data-id="element-2712" />
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-2713">
        {isOpen && <motion.div key="content" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-2714">
            <div className="border-t border-gray-100 px-5 py-5 bg-gray-50/50" data-id="element-2715">
              {isEmpty ? <div className="text-center py-8" data-id="element-2716">
                  <p className="font-nunito text-gray-400 text-sm" data-id="element-2717">
                    {isEs ? 'Creadores próximamente — ¡mantente atento!' : 'Creators coming soon — stay tuned!'}
                  </p>
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2718">
                  {accounts.map((account, i) => <SocialCard key={account.handle} account={account} index={i} data-id="element-2719" />)}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
type FilterType = 'All' | 'Todos' | 'Instagram' | 'TikTok' | 'YouTube' | 'A–Z';
export function SocialMediaVoices() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [filter, setFilter] = useState<FilterType>(isEs ? 'Todos' : 'All');
  const filters: FilterType[] = isEs ? ['Todos', 'Instagram', 'TikTok', 'YouTube', 'A–Z'] : ['All', 'Instagram', 'TikTok', 'YouTube', 'A–Z'];
  // Form State
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [instagram, setInstagram] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [youtube, setYoutube] = useState('');
  const [xHandle, setXHandle] = useState('');
  const [facebook, setFacebook] = useState('');
  const [spotify, setSpotify] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const resetForm = () => {
    setName('');
    setInstagram('');
    setTiktok('');
    setYoutube('');
    setXHandle('');
    setFacebook('');
    setSpotify('');
    setPhone('');
    setEmail('');
    setCategory('');
    setDescription('');
    setSubmitted(false);
  };
  const INFLUENCER_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbzuw7-2_a9uswZei5vcWrgBvR1KxUY0lnuypozlyZvfkc9MNxJKlrw0NiDC7DQpHAj-/exec';
  const handleSubmit = () => {
    const data = {
      name,
      instagram,
      tiktok,
      youtube,
      x: xHandle,
      facebook,
      spotify,
      phone,
      email,
      category,
      description,
      timestamp: new Date().toISOString()
    };
    fetch(INFLUENCER_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body: JSON.stringify(data)
    }).catch(err => {
      console.warn('Creator Google Sheets submission error:', err);
    });
    setSubmitted(true);
  };
  // First filter by platform
  const platformFilteredAccounts = accounts.filter(account => filter === 'All' || filter === 'Todos' || filter === 'A–Z' || account.platform === filter);
  // For A–Z view, sort alphabetically by name
  const sortedAccounts = filter === 'A–Z' ? [...platformFilteredAccounts].sort((a, b) => a.name.localeCompare(b.name)) : platformFilteredAccounts;
  return <section id="social-voices" className="bg-gray-50/50 py-16 lg:py-24" aria-labelledby="social-voices-heading" data-id="element-2720">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2721">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-12" data-id="element-2722">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-2723">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold flex items-center justify-center gap-2" data-id="element-2724">
              <SmartphoneIcon className="w-4 h-4" data-id="element-2725" />
              {isEs ? 'Comunidad Digital' : 'Digital Community'}
            </span>
            <h2 id="social-voices-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-2726">
              {isEs ? 'Voces Destacadas' : 'Uplifting Voices'}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2727">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2728" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-2729" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-2730" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-2731">
              {isEs ? 'Descubre y conecta con creadores, organizaciones y líderes hondureño-americanos notables que están generando un impacto en las plataformas de redes sociales.' : 'Discover and connect with notable Honduran-American creators, organizations, and leaders making an impact across social media platforms.'}
            </p>
            <div className="mt-5 max-w-2xl mx-auto bg-haa-skyblue/10 border border-haa-skyblue/30 rounded-xl px-5 py-4 text-sm font-nunito text-gray-700 leading-relaxed" data-id="element-2732">
              {isEs ? 'Este directorio tiene fines informativos. La inclusión no constituye un respaldo ni una promoción de ningún influencer, creador o su contenido en particular. Curamos esta lista para destacar voces hondureño-americanas notables en las redes sociales. Si tiene preguntas o inquietudes, comuníquese con nosotros — con gusto le ayudaremos.' : 'This directory is meant to be informational. Inclusion does not constitute an endorsement or promotion of any particular influencer, creator, or their content. We curate this list to highlight notable Honduran-American voices across social media.'}
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="flex justify-center mb-10" data-id="element-2733">
          <div className="inline-flex flex-wrap justify-center gap-1 bg-white p-1.5 rounded-xl shadow-sm border border-gray-100" data-id="element-2734">
            {filters.map(f => <button key={f} onClick={() => setFilter(f)} className={`relative px-4 sm:px-6 py-2 text-xs sm:text-sm font-nunito font-bold rounded-lg transition-colors ${filter === f ? 'text-white' : 'text-gray-500 hover:text-haa-navy'}`} data-id="element-2735">
                {filter === f && <motion.div layoutId="activeFilter" className="absolute inset-0 bg-haa-navy rounded-lg" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-2736" />}
                <span className="relative z-10" data-id="element-2737">{f}</span>
              </button>)}
          </div>
        </motion.div>

        {/* Featured Creator of the Month */}
        <FeaturedSpotlight data-id="element-2738" />

        {/* Category Accordions or A–Z Grid */}
        <div className="max-w-5xl mx-auto" data-id="element-2739">
          {filter === 'A–Z' ? <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2740">
              <AnimatePresence mode="popLayout" data-id="element-2741">
                {sortedAccounts.map((account, i) => <SocialCard key={account.handle} account={account} index={i} data-id="element-2742" />)}
              </AnimatePresence>
            </motion.div> : <>
              {CATEGORIES.map((category, i) => {
            const categoryAccounts = sortedAccounts.filter(a => a.categoryId === category.id);
            return <CategoryAccordion key={category.id} category={category} accounts={categoryAccounts} index={i} data-id="element-2743" />;
          })}
            </>}
        </div>

        {/* Footer CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.7
      }} className="mt-12 max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-haa-blue/5 border border-haa-blue/20 rounded-2xl p-6" data-id="element-2744">
          <div className="flex items-start gap-4" data-id="element-2745">
            <SmartphoneIcon className="w-6 h-6 text-haa-blue flex-shrink-0 mt-0.5" data-id="element-2746" />
            <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2747">
              <span className="font-bold text-haa-navy block mb-1" data-id="element-2748">
                {isEs ? '¿Conoce a un creador o influencer hondureño-americano que deberíamos destacar?' : 'Know a Honduran-American creator or influencer we should feature?'}
              </span>
              {isEs ? 'Si usted es o conoce a un creador notable de redes sociales hondureño-americano, envíelo para consideración en nuestro directorio.' : 'If you are or know a notable Honduran-American social media creator, submit them for consideration in our directory.'}
            </p>
          </div>
          <button onClick={() => setModalOpen(true)} className="flex-shrink-0 w-full sm:w-auto py-2.5 px-5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors whitespace-nowrap" data-id="element-2749">
            {isEs ? 'Sugerir un Creador' : 'Submit a Creator'}
          </button>
        </motion.div>
      </div>
    </section> /* Form Modal */;
  {
    /* Form Modal */
  }
  ;
  <AnimatePresence data-id="element-2750">
    {modalOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => {
      setModalOpen(false);
      if (submitted) resetForm();
    }} data-id="element-2751">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2752">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-2753">
            <div className="flex items-center gap-3" data-id="element-2754">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-midblue flex items-center justify-center shadow-sm" data-id="element-2755">
                <SmartphoneIcon className="w-5 h-5 text-white" data-id="element-2756" />
              </div>
              <h2 className="font-bebas text-2xl sm:text-3xl text-haa-navy tracking-wider leading-none pt-1" data-id="element-2757">
                {isEs ? 'Sugerir un Creador o Influencer' : 'Submit a Creator or Influencer'}
              </h2>
            </div>
            <button onClick={() => {
            setModalOpen(false);
            if (submitted) resetForm();
          }} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0" aria-label={isEs ? 'Cerrar' : 'Close'} data-id="element-2758">
              <XIcon className="w-5 h-5" data-id="element-2759" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-2760">
            {submitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center justify-center py-10 text-center" data-id="element-2761">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4" data-id="element-2762">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-2763">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-2764" />
                  </svg>
                </div>
                <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-2" data-id="element-2765">
                  {isEs ? '¡Gracias por su Sugerencia!' : 'Thank You for Your Submission!'}
                </h3>
                <p className="font-nunito text-gray-600 text-sm max-w-md" data-id="element-2766">
                  {isEs ? 'Hemos recibido la información del creador. Nuestro equipo la revisará para considerarla en nuestro directorio de Voces Destacadas.' : "We've received the creator information. Our team will review it for consideration in our Uplifting Voices directory."}
                </p>
              </motion.div> : <div className="space-y-5" data-id="element-2767">
                <p className="font-nunito text-gray-500 text-sm" data-id="element-2768">
                  {isEs ? 'Complete el formulario a continuación para sugerir un creador hondureño-americano notable. Todos los campos son opcionales.' : 'Fill out the form below to suggest a notable Honduran-American creator. All fields are optional.'}
                </p>

                <div data-id="element-2769">
                  <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2770">
                    {isEs ? 'Nombre / Nombre de la Cuenta' : 'Name / Account Name'}
                  </label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2771" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2772">
                  <div data-id="element-2773">
                    <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2774">
                      <InstagramIcon className="w-4 h-4 text-pink-500" data-id="element-2775" />
                      {isEs ? 'Usuario de Instagram' : 'Instagram Handle'}
                    </label>
                    <input type="text" value={instagram} onChange={e => setInstagram(e.target.value)} placeholder={isEs ? '@usuario' : '@username'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2776" />
                  </div>
                  <div data-id="element-2777">
                    <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2778">
                      <TikTokIcon className="w-4 h-4 text-gray-800" data-id="element-2779" />
                      {isEs ? 'Usuario de TikTok' : 'TikTok Handle'}
                    </label>
                    <input type="text" value={tiktok} onChange={e => setTiktok(e.target.value)} placeholder={isEs ? '@usuario' : '@username'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2780" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2781">
                  <div data-id="element-2782">
                    <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2783">
                      <YoutubeIcon className="w-4 h-4 text-red-500" data-id="element-2784" />
                      {isEs ? 'Canal de YouTube' : 'YouTube Channel'}
                    </label>
                    <input type="text" value={youtube} onChange={e => setYoutube(e.target.value)} placeholder={isEs ? '@canal o URL' : '@channel or URL'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2785" />
                  </div>
                  <div data-id="element-2786">
                    <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2787">
                      <XLogoIcon className="w-4 h-4 text-gray-800" data-id="element-2788" />
                      {isEs ? 'Usuario de X / Twitter' : 'X / Twitter Handle'}
                    </label>
                    <input type="text" value={xHandle} onChange={e => setXHandle(e.target.value)} placeholder={isEs ? '@usuario' : '@username'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2789" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2790">
                  <div data-id="element-2791">
                    <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2792">
                      <FacebookIcon className="w-4 h-4 text-blue-600" data-id="element-2793" />
                      Facebook
                    </label>
                    <input type="text" value={facebook} onChange={e => setFacebook(e.target.value)} placeholder={isEs ? 'Nombre de la página o URL' : 'Page name or URL'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2794" />
                  </div>
                  <div data-id="element-2795">
                    <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2796">
                      <SpotifyIcon className="w-4 h-4 text-green-500" data-id="element-2797" />
                      Spotify
                    </label>
                    <input type="text" value={spotify} onChange={e => setSpotify(e.target.value)} placeholder={isEs ? 'Nombre del artista o URL' : 'Artist name or URL'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2798" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2799">
                  <div data-id="element-2800">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2801">
                      {isEs ? 'Teléfono' : 'Phone'}
                    </label>
                    <input type="tel" value={phone} onChange={e => setPhone(formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2802" />
                  </div>
                  <div data-id="element-2803">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2804">
                      {isEs ? 'Correo Electrónico' : 'Email'}
                    </label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={isEs ? 'correo@ejemplo.com' : 'email@example.com'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-2805" />
                  </div>
                </div>

                <div data-id="element-2806">
                  <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2807">
                    {isEs ? 'Categoría' : 'Category'}
                  </label>
                  <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm bg-white" data-id="element-2808">
                    <option value="" data-id="element-2809">
                      {isEs ? '-- Seleccione --' : '-- Select --'}
                    </option>
                    {CATEGORIES.map(c => <option key={c.id} value={c.label} data-id="element-2810">
                        {isEs ? c.labelEs : c.label}
                      </option>)}
                    <option value="Other" data-id="element-2811">{isEs ? 'Otro' : 'Other'}</option>
                  </select>
                </div>

                <div data-id="element-2812">
                  <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-2813">
                    {isEs ? 'Breve Descripción' : 'Brief Description'}
                  </label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder={isEs ? '¿Por qué debería destacarse a este creador?' : 'Why should this creator be featured?'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm resize-none" data-id="element-2814" />
                </div>
              </div>}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3" data-id="element-2815">
            {submitted ? <button onClick={() => {
            setModalOpen(false);
            resetForm();
          }} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-2816">
                {isEs ? 'Cerrar' : 'Close'}
              </button> : <>
                <button onClick={() => setModalOpen(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-100 font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-2817">
                  {isEs ? 'Cancelar' : 'Cancel'}
                </button>
                <button onClick={handleSubmit} className="flex-1 py-2.5 bg-gradient-to-r from-haa-blue to-haa-navy hover:from-haa-midblue hover:to-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-all" data-id="element-2818">
                  {isEs ? 'Enviar' : 'Submit'}
                </button>
              </>}
          </div>
        </motion.div>
      </motion.div>}
  </AnimatePresence>;
}